import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  selectCurrentBilling,
  selectDashboardStats,
  selectDownloadingCompaniesMonthlyBillingCsv,
  selectDownloadingCompaniesStatsCsv,
  selectDownloadingCompanyStatsCsv,
} from '../../../state/admin.selector';
import { AsyncPipe, CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import { DateUtils } from '../../../../../common/helpers/date-utils';
import { NgApexchartsModule } from 'ng-apexcharts';
import { ChartCardComponent } from '../../components/chart-card/chart-card.component';
import {
  ChartData,
  getChartSeriesFromFiles,
} from '../../../../../common/helpers/chart-utils';
import { map, Observable } from 'rxjs';
import { ChartFileInfo } from '../../../domain/models/file/chart-file-info';
import { DashboardStats } from '../../../domain/models/dashboard-stats';
import {
  downloadCompaniesStatsCsv,
  downloadCompanyStatsCsv,
  downloadMonthlyBillingCsv,
  loadCurrentBilling,
  loadStatsForPeriod,
  loadStatsForPeriodAdmin,
} from '../../../state/admin.actions';
import { FormsModule } from '@angular/forms';
import { NgxDaterangepickerBootstrapDirective } from 'ngx-daterangepicker-bootstrap';
import moment from 'moment';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatepickerUtils } from '../../../../../common/helpers/date-picker-utils';
import { environment } from '../../../../../../environments/environment';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    AsyncPipe,
    NgApexchartsModule,
    ChartCardComponent,
    FormsModule,
    NgxDaterangepickerBootstrapDirective,
    TranslatePipe,
    DatePipe,
    CurrencyPipe,
    NgClass,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  @ViewChild(NgxDaterangepickerBootstrapDirective, { static: false })
  pickerDirective!: NgxDaterangepickerBootstrapDirective;
  chartFiles$!: Observable<ChartFileInfo[]>;
  chartData$!: Observable<ChartData>;
  defaultDateRange = {
    startDate: moment().subtract(1, 'year').startOf('day'),
    endDate: moment().endOf('day'),
  };
  selectedDates:
    | { startDate: moment.Moment; endDate: moment.Moment }
    | undefined;
  protected readonly dateUtils = inject(DateUtils);
  protected datePickerUtils = inject(DatepickerUtils);
  protected translate = inject(TranslateService);
  protected datepickerLocale = {};
  protected readonly environment = environment;
  protected currentLang: string | undefined;
  protected currencyCode: string | undefined;
  private store = inject(Store);
  protected dashboardStats$ = this.store.select(selectDashboardStats);
  protected currentBilling$ = this.store.select(selectCurrentBilling);
  protected isDownloadingCompanyStats$ = this.store.select(
    selectDownloadingCompanyStatsCsv,
  );
  protected isDownloadingCompaniesStats$ = this.store.select(
    selectDownloadingCompaniesStatsCsv,
  );
  protected isDownloadingMonthlyBilling$ = this.store.select(
    selectDownloadingCompaniesMonthlyBillingCsv,
  );

  get isFilterApplied(): boolean {
    return (
      this.selectedDates !== undefined &&
      (!this.selectedDates.startDate.isSame(
        this.defaultDateRange.startDate,
        'day',
      ) ||
        !this.selectedDates.endDate.isSame(
          this.defaultDateRange.endDate,
          'day',
        ))
    );
  }

  downloadStatsCsv() {
    const startDate =
      this.selectedDates?.startDate?.startOf('day')?.toDate() ??
      this.defaultDateRange.startDate.toDate();
    const endDate =
      this.selectedDates?.endDate?.endOf('day')?.toDate() ??
      this.defaultDateRange.endDate.toDate();

    if (environment.isSuperAdmin) {
      this.store.dispatch(downloadCompaniesStatsCsv({ startDate, endDate }));
    } else {
      this.store.dispatch(downloadCompanyStatsCsv({ startDate, endDate }));
    }
  }

  downloadMonthlyBillingCsv() {
    if (!environment.isSuperAdmin) {
      this.store.dispatch(downloadMonthlyBillingCsv());
    }
  }

  ngOnInit(): void {
    this.datePickerUtils.getDatepickerLocale().subscribe((locale) => {
      this.datepickerLocale = locale;

      if (environment.isSuperAdmin) {
        this.store.dispatch(
          loadStatsForPeriodAdmin({
            startDate: this.defaultDateRange.startDate.toDate(),
            endDate: this.defaultDateRange.endDate.toDate(),
          }),
        );
      } else {
        this.store.dispatch(
          loadStatsForPeriod({
            startDate: this.defaultDateRange.startDate.toDate(),
            endDate: this.defaultDateRange.endDate.toDate(),
          }),
        );
      }

      this.chartFiles$ = this.dashboardStats$.pipe(
        map((stats: DashboardStats | null) => stats?.chartFiles ?? []),
      );

      this.chartData$ = getChartSeriesFromFiles(
        this.chartFiles$,
        this.defaultDateRange.startDate.toDate(),
        this.defaultDateRange.endDate.toDate(),
      );
    });

    this.translate.onLangChange.subscribe(() => {
      this.updateLocale();
    });

    if (!environment.isSuperAdmin) {
      this.store.dispatch(loadCurrentBilling());

      this.currentBilling$.subscribe((billing) => {
        if (billing) {
          this.currencyCode = billing.isInDollar ? 'USD' : 'BRL';
        }
      });

      this.currentLang = this.translate.currentLang || 'pt';
      this.translate.onLangChange.subscribe((event) => {
        this.currentLang = event.lang;
      });
    }
  }

  updateLocale() {
    this.datePickerUtils.getDatepickerLocale().subscribe((locale) => {
      this.datepickerLocale = locale;
    });
  }

  onDateRangeChange(selected: any) {
    if (!selected?.startDate || !selected?.endDate) return;

    const startDate = selected.startDate.startOf('day').toDate();
    const endDate = selected.endDate.endOf('day').toDate();

    this.selectedDates = {
      startDate: moment(startDate),
      endDate: moment(endDate),
    };

    if (environment.isSuperAdmin) {
      this.store.dispatch(
        loadStatsForPeriodAdmin({
          startDate,
          endDate,
        }),
      );
    } else {
      this.store.dispatch(
        loadStatsForPeriod({
          startDate,
          endDate,
        }),
      );
    }

    this.chartFiles$ = this.dashboardStats$.pipe(
      map((stats: DashboardStats | null) => stats?.chartFiles ?? []),
    );

    this.chartData$ = getChartSeriesFromFiles(
      this.chartFiles$,
      startDate,
      endDate,
    );
  }

  clearFilters(): void {
    this.selectedDates = undefined;

    if (environment.isSuperAdmin) {
      this.store.dispatch(
        loadStatsForPeriodAdmin({
          startDate: this.defaultDateRange.startDate.toDate(),
          endDate: this.defaultDateRange.endDate.toDate(),
        }),
      );
    } else {
      this.store.dispatch(
        loadStatsForPeriod({
          startDate: this.defaultDateRange.startDate.toDate(),
          endDate: this.defaultDateRange.endDate.toDate(),
        }),
      );
    }

    if (this.pickerDirective) {
      this.pickerDirective.clear();
    }

    this.chartFiles$ = this.dashboardStats$.pipe(
      map((stats: DashboardStats | null) => stats?.chartFiles ?? []),
    );

    this.chartData$ = getChartSeriesFromFiles(
      this.chartFiles$,
      this.defaultDateRange.startDate.toDate(),
      this.defaultDateRange.endDate.toDate(),
    );
  }
}
